import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Card, useTabParam } from '@structural-climbing/ui'
import { type Discipline, disciplineForSection } from '@structural-climbing/validation'
import { useGymDisciplines } from '../layouts/useGymDisciplines'
import { RoutesGrid } from '../features/routes/RoutesGrid'
import { RouteGradeChart } from '../features/routes/RouteGradeChart'
import { RouteDistributionComparisonChart } from '../features/routes/RouteDistributionComparisonChart'
import { ListPages } from '../components/ListPages'
import type { ClimbingRoute, GymSection } from '../features/routes/types'
import { ChartCarousel } from '../components/ChartCarousel'

const ROUTE_TABS = ['active', 'archived', 'sections'] as const
type Tab = (typeof ROUTE_TABS)[number]

interface GradeStat {
  grade: number
  count: number
  /** Count per section id ('' for no section), from by_section=1. */
  sections?: Record<string, number>
}

const PAGE_SIZE = 20

export function RoutesPage() {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // In the URL, so a refresh stays on Archived or Sections.
  const [tab, setTab] = useTabParam<Tab>('tab', ROUTE_TABS, 'active')
  // Only the disciplines this gym has. With one, there is nothing to switch
  // between, and a ?discipline= naming the other falls back to it.
  const disciplines = useGymDisciplines()
  // In the URL (?discipline=lead) so a refresh keeps you on the Lead tab.
  const [discipline, setDiscipline] = useTabParam('discipline', disciplines, disciplines[0])
  const [routes, setRoutes] = useState<ClimbingRoute[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [gradeStats, setGradeStats] = useState<GradeStat[]>([])
  const [gradeStatsLoading, setGradeStatsLoading] = useState(true)
  const [sections, setSections] = useState<GymSection[]>([])
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)

  const loadPage = useCallback(
    async (status: Tab, pageNumber: number, sectionId: string | null = null) => {
      if (status === 'sections') return
      const pageOffset = (pageNumber - 1) * PAGE_SIZE
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const sectionParam = sectionId ? `&section_id=${sectionId}` : ''
        const res = await fetch(
          `${apiUrl}/climbing-routes?status=${status}&limit=${PAGE_SIZE}&offset=${pageOffset}&discipline=${discipline}${sectionParam}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (res.ok) {
          const body = (await res.json()) as { routes: ClimbingRoute[]; total?: number }
          setRoutes(body.routes)
          setTotal(body.total ?? pageOffset + body.routes.length)
          setPage(pageNumber)
        }
      } finally {
        setLoading(false)
      }
    },
    [getAccessTokenSilently, apiUrl, discipline],
  )

  const loadGradeStats = useCallback(
    async (status: Tab, silent = false) => {
      if (status === 'sections') return
      // Silent keeps the chart on screen, so its height doesn't change and jump the scroll.
      if (!silent) setGradeStatsLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(
          // Both disciplines, per section, in one answer: the chart then follows
          // the section pills and the discipline cards without asking again.
          `${apiUrl}/climbing-routes/current-grade-stats?status=${status}&by_section=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (res.ok) setGradeStats((await res.json()) as GradeStat[])
      } finally {
        setGradeStatsLoading(false)
      }
    },
    [getAccessTokenSilently, apiUrl],
  )

  const loadSections = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-sections`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setSections((await res.json()) as GymSection[])
    } catch {
      // non-critical; sections stay empty
    }
  }, [getAccessTokenSilently, apiUrl])

  useEffect(() => {
    void loadSections()
  }, [loadSections])

  useEffect(() => {
    setRoutes([])
    void loadPage(tab, 1, selectedSectionId)
  }, [tab, selectedSectionId, loadPage])

  // Not on a section or discipline change: those are worked out from this.
  useEffect(() => {
    void loadGradeStats(tab)
  }, [tab, loadGradeStats])

  // After a save: the same page again, so the setter stays where they were.
  function handleRefresh() {
    void loadPage(tab, page, selectedSectionId)
    void loadGradeStats(tab)
  }

  // A route was archived or restored, so it left this tab. Drop it in place,
  // with no reload; the next page picked is read fresh anyway.
  function handleRouteRemoved(routeId: string) {
    setRoutes((prev) => prev.filter((r) => r.id !== routeId))
    setTotal((n) => Math.max(0, n - 1))
    void loadGradeStats(tab, true)
  }

  // Switch discipline tab. Section ids are per-discipline, so drop the section
  // filter; loadPage/loadGradeStats depend on `discipline`, so the load effect
  // refetches the list, charts, and stats for the new discipline.
  function handleDisciplineChange(next: Discipline) {
    if (next === discipline) return
    setDiscipline(next)
    setSelectedSectionId(null)
    setRoutes([])
  }

  // Sections belonging to the active discipline (rope => lead, else boulder).
  const disciplineSections = sections.filter(
    (s) => disciplineForSection(s.discipline) === discipline,
  )
  // Sections of a discipline the gym no longer has are kept, but not shown.
  const gymSections = sections.filter((s) =>
    disciplines.includes(disciplineForSection(s.discipline)),
  )

  // Which discipline each section belongs to; routes with no section are boulders,
  // the same rule the Worker uses.
  const sectionDiscipline = new Map(sections.map((s) => [s.id, disciplineForSection(s.discipline)]))
  const disciplineOf = (sectionKey: string): Discipline =>
    (sectionKey ? sectionDiscipline.get(sectionKey) : undefined) ?? 'boulder'
  const countWhere = (g: GradeStat, keep: (sectionKey: string) => boolean) =>
    g.sections
      ? Object.entries(g.sections).reduce((n, [key, c]) => (keep(key) ? n + c : n), 0)
      : g.count
  const shownStats = gradeStats.map((g) => ({
    grade: g.grade,
    count: countWhere(g, (key) =>
      selectedSectionId ? key === selectedSectionId : disciplineOf(key) === discipline,
    ),
  }))
  const disciplineTotal = (d: Discipline) =>
    gradeStats.reduce((n, g) => n + countWhere(g, (key) => disciplineOf(key) === d), 0)

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <RoutesGrid
        routes={routes}
        // Only before the first page: a page change keeps the list on screen.
        loading={loading && routes.length === 0}
        tab={tab}
        onTabChange={(next) => {
          if (next === tab) return
          setTab(next)
          setRoutes([])
        }}
        onRefresh={handleRefresh}
        onRouteRemoved={handleRouteRemoved}
        apiUrl={apiUrl}
        discipline={discipline}
        sections={gymSections}
        filterSections={disciplineSections}
        onSectionsChange={loadSections}
        selectedSectionId={selectedSectionId}
        onSectionFilter={setSelectedSectionId}
        aboveTabs={
          // Two cards, so the discipline never reads as one more list tab.
          disciplines.length > 1 && (
            <div
              role="group"
              aria-label={t('routes.sectionDiscipline')}
              className="grid grid-cols-2 gap-3"
            >
              {(
                [
                  ['boulder', t('routes.disciplineBouldering')],
                  ['lead', t('routes.disciplineLead')],
                ] as const
              ).map(([key, label]) => {
                const on = discipline === key
                return (
                  <Card
                    key={key}
                    isPressable
                    shadow="none"
                    aria-pressed={on}
                    onPress={() => handleDisciplineChange(key)}
                    className={`border px-4 py-3 text-left ${
                      on
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-divider bg-content1'
                    }`}
                  >
                    <span className="text-base font-semibold">{label}</span>
                    <span className="text-sm text-foreground/55">
                      {gradeStatsLoading
                        ? ' '
                        : t('routes.totalRoutes', { count: disciplineTotal(key) })}
                    </span>
                  </Card>
                )
              })}
            </div>
          )
        }
        charts={
          // One card, one chart at a time: the list stays near the top.
          <ChartCarousel
            slides={[
              {
                key: 'grades',
                label: t('routes.chartTitle'),
                content: (
                  <RouteGradeChart
                    gradeStats={shownStats}
                    loading={gradeStatsLoading}
                    total={shownStats.reduce((s, g) => s + g.count, 0)}
                    discipline={discipline}
                  />
                ),
              },
              {
                key: 'trend',
                label: t('routes.comparisonChartTitle'),
                content: (
                  <RouteDistributionComparisonChart
                    apiUrl={apiUrl}
                    getAccessTokenSilently={getAccessTokenSilently}
                    discipline={discipline}
                  />
                ),
              },
            ]}
          />
        }
      />
      {tab !== 'sections' && (
        <ListPages
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={(next) => void loadPage(tab, next, selectedSectionId)}
          isLoading={loading}
        />
      )}
    </div>
  )
}
